/**
 * 위치 드롭다운에 무엇을 늘어놓을 것인가 — 순수 계산.
 *
 * 위치는 별도 엔티티가 아니라 화면에 적힌 문자열이라, "등록된 위치"란 지금 어느
 * 화면엔가 적혀 있는 이름을 말한다. 그래서 목록을 만드는 규칙이 곧 이 기능의 계약이다.
 *
 * **지금 값은 목록에 없어도 반드시 남긴다.** 자유 텍스트로 적던 시절의 값이나, 다른
 * 사람이 방금 바꾼 값은 서버에서 받아 온 목록에 없을 수 있다. 그것을 빼고 그리면
 * 드롭다운이 다른 이름을 고른 것처럼 보이고, 사용자가 저장을 누르는 순간 **원래
 * 위치가 조용히 바뀐다.** 고르지도 않은 값으로 바뀌는 것이라 되돌릴 실마리도 없다.
 */

/**
 * "새 위치 직접 입력" 을 고르면 오는 값.
 *
 * **앞에 공백을 둔 것이 이 값의 안전장치다.** `locationChoices` 는 이름을 모두
 * `trim` 해서 내놓으므로, 앞뒤에 공백이 붙은 문자열은 **선택지로 절대 만들어지지
 * 않는다.** 따라서 이 표식이 실제 위치 이름과 같아지는 일이 없다.
 *
 * 처음에는 NUL 문자를 쓰려 했다. 부딪힐 일이 확실히 없다는 점은 좋지만, `<option>`
 * 의 값으로 NUL 을 DOM 에 실어 보내는 것이 브라우저마다 같게 동작하는지를 여기서
 * 확인할 방법이 없었다 — **검증할 수 없는 것을 믿고 쓰지 않는다.** 평범한 ASCII 로
 * 같은 보장을 얻을 수 있으면 그쪽이 낫다.
 *
 * 누군가 API 로 위치를 이 값과 똑같이 적어 넣으면 그 화면은 드롭다운이 입력 모드로
 * 열린다. 값이 사라지거나 바뀌지는 않으므로 되돌릴 수 있다.
 */
export const NEW_LOCATION = ' __new__';

/**
 * 위치 이름의 순서 — **가나다순, 숫자는 크기대로**("본사 2층" 이 "본사 10층" 앞).
 *
 * 그냥 `localeCompare` 는 숫자도 글자로 견주어 "10층" 이 "2층" 앞에 섰다. 위치 이름에는 층·동·호기·공장 번호가
 * 흔해 드롭다운에서 찾기 어려웠다(2026-10-19 사용자 지시). 드롭다운·화면 목록 위치 필터(서버 `usedLocations`)·
 * 사업장 나무·모니터링 벽의 위치 묶음이 모두 이 순서를 쓴다 — 한 화면에서 둘이 다르게 늘어서면 같은 위치를 두 번 찾는다.
 */
export function compareLocationNames(a: string, b: string): number {
  return a.localeCompare(b, 'ko', { numeric: true });
}

/**
 * 드롭다운에 늘어놓을 위치 이름들.
 *
 * 앞뒤 공백을 다듬고, 빈 것을 버리고, 겹치는 것을 하나로 하고, 가나다순(숫자는 크기대로)으로 세운다.
 * 지금 값이 목록에 없으면 끼워 넣는다.
 */
export function locationChoices(known: readonly string[], current: string): string[] {
  const names = new Set<string>();

  for (const name of known) {
    const trimmed = name.trim();
    if (trimmed !== '') names.add(trimmed);
  }

  const currentTrimmed = current.trim();
  if (currentTrimmed !== '') names.add(currentTrimmed);

  return [...names].sort(compareLocationNames);
}

/**
 * 고른 값이 "직접 입력" 인가.
 *
 * 이것은 **위치를 지운 것이 아니다.** 그대로 빈 문자열로 바꿔 버리면 적으려고 누른
 * 사람의 기존 위치가 사라지므로, 그때는 값을 건드리지 않고 입력 모드로만 넘어간다는
 * 사실을 호출하는 쪽이 알아야 한다.
 */
export function isNewLocation(value: string): boolean {
  return value === NEW_LOCATION;
}
